/**
 * ConciergePlaceCombobox
 * ──────────────────────
 * Picks a saved hotel or destination and hands the whole record back, so the
 * request form can fill in the name, the exact spot label and the pin at once.
 *
 * Why not a plain <Select>
 * ───────────────────────
 * The operator is on the phone. They know the property by name and want to type
 * three letters and hit enter, not scroll a list. A select also gives no room
 * for the second line of context — which spot at the property, and whether it
 * even has a pin — which is the information that decides whether they need to
 * touch the map at all.
 *
 * Busy accounts sort first rather than everything sorting alphabetically: a
 * hotel that calls daily should not be below a one-off from last season.
 */

import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { ConciergePlace } from '@/lib/adminApi';
import { ChevronsUpDown, MapPin, MapPinOff, Star } from 'lucide-react';

export interface ConciergePlaceComboboxProps {
  places: ConciergePlace[];
  kind: 'hotel' | 'destination';
  /** Shown on the trigger when nothing is chosen. */
  placeholder: string;
  onPick: (place: ConciergePlace) => void;
}

export function ConciergePlaceCombobox({
  places,
  kind,
  placeholder,
  onPick,
}: ConciergePlaceComboboxProps) {
  const [open, setOpen] = useState(false);

  const options = useMemo(
    () =>
      places
        .filter((p) => p.kind === kind && p.is_active)
        .sort((a, b) => b.use_count - a.use_count || a.name.localeCompare(b.name)),
    [places, kind],
  );

  if (options.length === 0) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between bg-gray-950 border-gray-800 text-gray-300 hover:text-white font-normal"
        >
          <span className="truncate">{placeholder}</span>
          <ChevronsUpDown className="w-3.5 h-3.5 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 bg-gray-900 border-gray-800 w-[--radix-popover-trigger-width]">
        <Command className="bg-gray-900">
          <CommandInput
            placeholder={kind === 'hotel' ? 'Search hotels…' : 'Search destinations…'}
            className="text-white"
          />
          <CommandList>
            <CommandEmpty className="py-4 text-center text-sm text-gray-500">
              Nothing saved by that name.
            </CommandEmpty>
            <CommandGroup>
              {options.map((p) => (
                <CommandItem
                  key={p.id}
                  // Searching should match the spot too — an operator may
                  // remember "Victoria Street" rather than the hotel's name.
                  value={`${p.name} ${p.spot_label ?? ''}`}
                  onSelect={() => {
                    onPick(p);
                    setOpen(false);
                  }}
                  className="text-gray-200 aria-selected:bg-gray-800"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate font-medium">{p.name}</span>
                      {p.default_priority === 'vip' ? (
                        <Star className="w-3 h-3 text-amber-400 shrink-0" />
                      ) : null}
                    </div>
                    <div className="flex items-center gap-1.5 text-[11px] text-gray-500 truncate">
                      {p.lat != null ? (
                        <MapPin className="w-3 h-3 text-emerald-500 shrink-0" />
                      ) : (
                        <MapPinOff className="w-3 h-3 shrink-0" />
                      )}
                      <span className="truncate">
                        {p.spot_label || (p.lat != null ? 'pinned' : 'no pin saved')}
                      </span>
                    </div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default ConciergePlaceCombobox;
